import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import { useFormikContext } from "formik";
import AddButton from "../buttons/button";
import { AlertTriangle, FileText, ShoppingCart, X } from "lucide-react";
import { handleShowErrorToast } from "../helper-components/helper-function";

// A navigate() that follows a successful save (e.g. draft → add doctor) must
// not be stopped by the guard. Call this right before such a navigate().
let bypassUntil = 0;
export const allowBillingNavigation = () => {
  bypassUntil = Date.now() + 1000;
};
const isBypassed = () => Date.now() < bypassUntil;

const GUARD_STATE_KEY = "billingLeaveGuard";

// Warns before leaving Add Billing with an unsaved bill:
//  - products, no customer  → data will be lost (draft needs a mobile number)
//  - products and customer → offer to save as draft
//  - no products            → no warning
//
// The app uses <BrowserRouter>, where react-router's useBlocker isn't
// available, so in-app navigation is caught on the router's navigator, the
// browser Back button through an extra history entry, and reload / tab close
// through beforeunload (the browser shows its own dialog there).
export default function BillingLeaveGuard({ enabled = true, handleBilling }) {
  const { values, validateForm, resetForm } = useFormikContext();
  const hasProducts = (values?.data?.length ?? 0) > 0;
  const hasCustomer = !!(values?.phone?.label || values?.phone?.value);
  const mode = !enabled || !hasProducts ? null : hasCustomer ? "draft" : "lose";

  const modeRef = useRef(mode);
  modeRef.current = mode;
  // true while our extra history entry sits on top of /add-bill
  const guardEntryRef = useRef(false);
  const [pending, setPending] = useState(null); // { mode, proceed, onStay }
  const [saving, setSaving] = useState(false);
  const { navigator } = useContext(UNSAFE_NavigationContext);

  const pushGuardEntry = () => {
    window.history.pushState(
      { ...window.history.state, [GUARD_STATE_KEY]: true },
      ""
    );
    guardEntryRef.current = true;
  };

  // Extra history entry, so Browser Back lands on this page and can be asked about
  useEffect(() => {
    if (mode && !guardEntryRef.current) pushGuardEntry();
  }, [mode]);

  // In-app navigation: links, sidebar, navigate(), header back arrow
  useEffect(() => {
    const { push, replace, go } = navigator;

    const doPush = (args) => {
      // Take the place of our extra entry instead of stacking on top of it
      if (guardEntryRef.current) {
        guardEntryRef.current = false;
        replace.apply(navigator, args);
      } else {
        push.apply(navigator, args);
      }
    };
    const doGo = (delta) => {
      if (delta < 0 && guardEntryRef.current) {
        guardEntryRef.current = false;
        go.call(navigator, delta - 1); // step over our extra entry too
      } else {
        go.call(navigator, delta);
      }
    };
    const intercept = (proceed) => {
      if (isBypassed() || !modeRef.current) return proceed();
      setPending({ mode: modeRef.current, proceed });
    };

    navigator.push = (...args) => intercept(() => doPush(args));
    navigator.replace = (...args) =>
      intercept(() => replace.apply(navigator, args));
    navigator.go = (delta) => intercept(() => doGo(delta));
    return () => {
      navigator.push = push;
      navigator.replace = replace;
      navigator.go = go;
    };
  }, [navigator]);

  // Browser Back: we've just left our extra entry and are still on /add-bill
  useEffect(() => {
    const onPopState = () => {
      if (!guardEntryRef.current) return;
      if (window.history.state?.[GUARD_STATE_KEY]) return;
      guardEntryRef.current = false;
      if (!modeRef.current) {
        // Nothing to protect any more: finish the Back the user asked for
        window.history.back();
        return;
      }
      setPending({
        mode: modeRef.current,
        proceed: () => window.history.back(),
        onStay: pushGuardEntry,
      });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Reload / close tab: browsers only allow their own generic dialog here
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!modeRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const stay = () => {
    if (saving) return;
    pending?.onStay?.();
    setPending(null);
  };

  const leave = () => {
    const { proceed } = pending;
    setPending(null);
    proceed();
  };

  const saveDraftAndLeave = async () => {
    if (saving) return;
    const errors = await validateForm();
    if (Object.keys(errors).length > 0) {
      handleShowErrorToast(errors);
      return;
    }
    const { proceed } = pending;
    setSaving(true);
    handleBilling({ ...values, submittedButton: "draft" }, resetForm, {
      onSaved: () => {
        setSaving(false);
        setPending(null);
        proceed();
      },
      onFailed: () => setSaving(false),
    });
  };

  // Freeze what the modal shows when it opens. It keeps rendering for its
  // fade-out after `pending` is cleared (and after a draft save resets the
  // form) — reading live state there flashes the other variant / 0 items.
  const shownRef = useRef(null);
  if (pending && shownRef.current?.pending !== pending) {
    shownRef.current = {
      pending,
      isDraft: pending.mode === "draft",
      itemCount: values?.data?.length ?? 0,
      customerName:
        values?.cmr_first_name ||
        (typeof values?.phone?.label === "string"
          ? values.phone.label.split(",")[0]
          : ""),
      total: parseFloat(values?.sot_total_amount || 0).toFixed(2),
    };
  }
  const { isDraft, itemCount, customerName, total } = shownRef.current || {};
  const primaryButtonRef = useRef(null);

  // Explicit colours: the Tailwind theme here replaces the default palette
  const tone = isDraft
    ? { icon: "#277DFE", bg: "#D1E9FF", ring: "#EFF8FF" }
    : { icon: "#DC6803", bg: "#FEF0C7", ring: "#FFFAEB" };
  const buttonBase =
    "flex-1 h-10 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed";
  const secondaryButtonClass = `${buttonBase} bg-[#FFFFFF] text-[#344054] border border-[#D0D5DD] shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:bg-[#F9FAFB]`;
  const primaryButtonClass = `${buttonBase} bg-[var(--primary-button-bg-color)] text-[var(--primary-button-fg-color)] border border-[var(--primary-button-bg-color)] shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:bg-[var(--primary-button-hover-color)]`;

  return (
    <Transition show={!!pending} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[60] overflow-y-auto"
        onClose={stay}
        // Focus the main action, not the first button (the ✕)
        initialFocus={primaryButtonRef}
      >
        <div className="flex items-center justify-center min-h-screen px-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-[rgba(16,24,40,0.45)] backdrop-blur-[2px]" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95 translate-y-2"
            enterTo="opacity-100 scale-100 translate-y-0"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="relative w-full max-w-[400px] p-6 bg-[#FFFFFF] rounded-2xl shadow-[0_20px_24px_-4px_rgba(16,24,40,0.08),0_8px_8px_-4px_rgba(16,24,40,0.03)]">
              <button
                type="button"
                aria-label="Close"
                disabled={saving}
                onClick={stay}
                className="absolute top-4 right-4 h-8 w-8 rounded-lg flex items-center justify-center border-0 outline-none text-[#98A2B3] hover:bg-[#F2F4F7] hover:text-[#667085] focus-visible:bg-[#F2F4F7] transition-colors"
              >
                <X size={18} />
              </button>

              <div
                className="h-12 w-12 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: tone.bg,
                  color: tone.icon,
                  boxShadow: `0 0 0 8px ${tone.ring}`,
                }}
              >
                {isDraft ? <FileText size={22} /> : <AlertTriangle size={22} />}
              </div>

              <Dialog.Title className="mt-5 text-[18px] leading-7 font-semibold text-[#101828]">
                {isDraft ? "Save this bill as a draft?" : "Leave without saving?"}
              </Dialog.Title>
              <p className="mt-1 text-sm leading-5 text-[#475467]">
                {isDraft
                  ? "You can pick it up later from Bills and finish the payment."
                  : "The products on this bill will be lost. To keep them, add the customer's mobile number and save as a draft."}
              </p>

              {/* What's on the bill right now */}
              <div className="mt-4 flex items-center gap-3 rounded-xl border border-[#EAECF0] bg-[#F9FAFB] px-3 py-2.5">
                <div className="h-8 w-8 shrink-0 rounded-lg bg-[#FFFFFF] border border-[#EAECF0] flex items-center justify-center text-[#667085]">
                  <ShoppingCart size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#344054] truncate">
                    {isDraft && customerName ? customerName : "No customer added"}
                  </div>
                  <div className="text-xs text-[#667085]">
                    {itemCount} {itemCount === 1 ? "item" : "items"}
                  </div>
                </div>
                <div className="text-sm font-semibold text-[#101828]">₹{total}</div>
              </div>

              <div className="flex gap-3 mt-6">
                {isDraft ? (
                  <>
                    <button type="button" disabled={saving} onClick={leave} className={secondaryButtonClass}>
                      Discard
                    </button>
                    <AddButton
                      ref={primaryButtonRef}
                      prefixIcon={false}
                      loading={saving}
                      onclkFn={saveDraftAndLeave}
                      text="Save draft"
                      className={primaryButtonClass}
                      spinnerClass="h-4 w-4 border-t-2 border-white"
                    />
                  </>
                ) : (
                  <>
                    <button type="button" onClick={leave} className={secondaryButtonClass}>
                      Leave anyway
                    </button>
                    <button
                      ref={primaryButtonRef}
                      type="button"
                      onClick={stay}
                      className={primaryButtonClass}
                    >
                      Keep editing
                    </button>
                  </>
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
